import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TimeOfDayBadge from "@/components/TimeOfDayBadge";
import { N8 } from "@/components/BrandMark";
import N8Emblem from "@/components/N8Emblem";
import { endpoints } from "@/lib/api";
import { Sparkles, Feather, Flame, Check, Search, X, Paperclip, ImagePlus, Trash2, Star, Pencil } from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { pickStoicPrompt } from "@/lib/stoicContent";
import CompassPatternNudge from "@/components/CompassPatternNudge";
import ProGate from "@/components/ProGate";
import ThemedWeekBanner from "@/components/ThemedWeekBanner";
import PageHero from "@/components/PageHero";
import CarryingAffirmationPanel from "@/components/CarryingAffirmationPanel";
import CarryingIntentionPanel from "@/components/CarryingIntentionPanel";
import { HERO } from "@/lib/heroImages";

const MOODS = ["luminous", "calm", "neutral", "restless", "warm", "tired"];

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [content, setContent] = useState("");
  const [mood, setMood] = useState("neutral");
  const [insight, setInsight] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [streak, setStreak] = useState(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  // Filter by entry_type. "all" | "Soul Work Reflection" | other future types.
  const [entryTypeFilter, setEntryTypeFilter] = useState("all");
  const navigate = useNavigate();

  // An entry is a "starred insight" if the member saved it via the Star
  // button on Home (Future You) or via the bookmark button on the N8 Guide
  // card in Rituals. Both write recognizable prompt prefixes.
  const isStarredInsight = (entry) => {
    const p = (entry?.prompt || "").trim();
    return p.startsWith("Future You — Insight") || p.startsWith("N8 Guide ·");
  };
  const isSoulWorkEntry = (entry) => (entry?.entry_type || "") === "Soul Work Reflection";
  const soulWorkCount = useMemo(() => entries.filter(isSoulWorkEntry).length, [entries]);
  const visibleEntries = useMemo(() => {
    let list = entries;
    if (entryTypeFilter === "soul-work") list = list.filter(isSoulWorkEntry);
    if (showStarredOnly) list = list.filter(isStarredInsight);
    return list;
  }, [entries, showStarredOnly, entryTypeFilter]);
  const starredCount = useMemo(() => entries.filter(isStarredInsight).length, [entries]);

  const load = async (q = "") => {
    const params = q.trim() ? { q: q.trim() } : {};
    const [e, s] = await Promise.all([endpoints.journal(params), endpoints.journalStreak()]);
    setEntries(e.data);
    setStreak(s.data);
  };
  useEffect(() => { load(); }, []);

  // Debounced search: refresh entries when the query changes.
  useEffect(() => {
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const params = query.trim() ? { q: query.trim() } : {};
        const { data } = await endpoints.journal(params);
        setEntries(data);
      } catch { /* silent */ }
      setSearching(false);
    }, 180);
    return () => clearTimeout(t);
  }, [query]);

  const newPrompt = async () => {
    setLoadingPrompt(true);
    try {
      const { data } = await endpoints.aiPrompt(mood);
      setPrompt(data.prompt);
    } catch { toast.error("The prompt is quiet."); }
    setLoadingPrompt(false);
  };

  const stoicPrompt = () => {
    // Local-only pick — no LLM cost, always available, calm and gender-neutral.
    setPrompt(pickStoicPrompt(prompt));
  };

  const getInsight = async () => {
    if (!content.trim()) return;
    setLoadingInsight(true);
    try {
      const { data } = await endpoints.aiInsight(content);
      setInsight(data.insight);
    } catch { toast.error("Insight not available."); }
    setLoadingInsight(false);
  };

  const save = async () => {
    if (!content.trim()) return;
    const prev = streak?.current_streak || 0;
    await endpoints.createJournal({ prompt, content, mood_tag: mood, attachments });
    setContent(""); setPrompt(""); setInsight(""); setAttachments([]);
    await load();
    // Re-read the new streak from state after load — small delay to let setState flush.
    setTimeout(async () => {
      try {
        const { data: fresh } = await endpoints.journalStreak();
        setStreak(fresh);
        if (fresh.current_streak > prev) {
          toast.success(
            fresh.current_streak === 1
              ? "A first day. The page remembers."
              : `${fresh.current_streak} days of cultivating. Beautifully done.`
          );
        } else {
          toast.success("Entry saved.");
        }
      } catch { toast.success("Entry saved."); }
    }, 150);
  };

  const attachPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "journal");
      const { data } = await endpoints.libraryUpload(fd);
      setAttachments((prev) => [...prev, data.storage_path]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not attach.");
    }
    setUploading(false);
  };

  return (
    <div className="space-y-12" data-testid="journal-page">
      <PageHero
        emblem=""
        eyebrow={
          <span className="inline-flex items-center gap-3">
            <span>Centered in Self</span>
            <span className="opacity-50" aria-hidden>·</span>
            <TimeOfDayBadge testid="journal-time-of-day" />
          </span>
        }
        title="The journal"
        supporting="A page that never asks you to be more than you are."
        body="Ask the guide for a prompt, or begin in silence. Nothing here is judged."
        image={HERO.journal}
        testid="journal-hero"
      />

      {/* Contextual "Today I'm carrying" panel — only renders when the
          member arrived here via an affirmation's Carry button. Reads
          once from sessionStorage and clears it, so plain Journal visits
          are untouched. */}
      <CarryingAffirmationPanel />

      {/* Sibling contextual panel — appears only when the member arrived
          via "Reflect in Journal →" from TODAY'S INTENTION. Reads once
          and clears; plain Journal visits are untouched. */}
      <CarryingIntentionPanel />

      <ThemedWeekBanner variant="journal" />

      {/* Journal Insights (Pro) — recurring themes + monthly reflection
          summaries. Free users see a soft invite; Pro sees the actual
          Compass Pattern Nudge which draws from their journal. */}
      <ProGate
        feature="journal_insights"
        title="Go deeper into your reflections."
        blurb="You already reflect, release, and make space for what's next. Pro reveals the patterns beneath — recurring themes across your entries, connections to your Soul Compass and Soul Work, and monthly reflections shaped around what you're actually writing about."
        bullets={[
          "Recurring theme detection",
          "Monthly journal reflections",
          "Connections to Soul Work and Becoming goals",
          "Personalized reflection prompts",
        ]}
        testid="pro-gate-journal_insights"
      >
        <CompassPatternNudge variant="compact" onExplored={() => navigate("/becoming?section=understand")} />
      </ProGate>

      <section className="grid lg:grid-cols-[1.4fr,1fr] gap-6 reveal reveal-1">
        <div className="p-8 border hairline rounded-md bg-[hsl(var(--paper))]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
              <N8Emblem
                mode="simplified"
                size={28}
                ariaLabel="N8ONE emblem"
                testid="journal-composer-mark"
                className="shrink-0"
              />
              <span>A prompt</span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={mood} onValueChange={setMood}>
                <SelectTrigger className="w-[140px] rounded-full bg-transparent" data-testid="mood-select">
                  <SelectValue placeholder="Mood" />
                </SelectTrigger>
                <SelectContent>
                  {MOODS.map((m) => (
                    <SelectItem key={m} value={m} data-testid={`mood-${m}`}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={newPrompt}
                disabled={loadingPrompt}
                data-testid="btn-new-prompt"
                variant="outline"
                className="rounded-full"
              >
                <Sparkles size={14} className="mr-2" />{loadingPrompt ? "Listening…" : "New prompt"}
              </Button>
              <Button
                onClick={stoicPrompt}
                data-testid="btn-stoic-prompt"
                variant="outline"
                className="rounded-full border-[hsl(var(--clay))]/40 text-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/10"
                title="A reflection rooted in Stoic principles"
              >
                <Feather size={14} className="mr-2" />
                Stoic prompt
              </Button>
            </div>
          </div>
          <p className="mt-6 font-display text-2xl leading-snug italic text-[hsl(var(--fg))] min-h-[3rem]" data-testid="prompt-text">
            {prompt || "What have you outgrown that no longer belongs to you?"}
          </p>

          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Begin here…"
            data-testid="journal-textarea"
            className="mt-6 min-h-[240px] bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))] font-[Manrope] text-base leading-relaxed"
          />

          {attachments.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="journal-attachments-preview">
              {attachments.map((p) => (
                <div key={p} className="relative w-16 h-16 rounded-md overflow-hidden border hairline bg-[hsl(var(--sand))]/40">
                  <img src={`${API}/files/${p}`} alt="attachment" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setAttachments((prev) => prev.filter((x) => x !== p))}
                    aria-label="Remove attachment"
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--paper))] grid place-items-center"
                  >
                    <Trash2 size={9} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3 justify-end items-center">
            <label className="mr-auto inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline text-sm tab-link cursor-pointer text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]" data-testid="btn-attach-label">
              <ImagePlus size={14} />
              {uploading ? "Attaching…" : "Attach photo"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; attachPhoto(f); }}
                data-testid="journal-attach-input"
              />
            </label>
            <Button
              onClick={getInsight}
              disabled={loadingInsight || !content.trim()}
              data-testid="btn-insight"
              variant="outline"
              className="rounded-full"
            >
              <Sparkles size={14} className="mr-2" />{loadingInsight ? "Reading…" : "Ask for insight"}
            </Button>
            <Button
              onClick={save}
              disabled={!content.trim()}
              data-testid="btn-save-journal"
              className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
            >
              Save entry
            </Button>
          </div>
        </div>

        <aside className="p-8 border hairline rounded-md bg-[hsl(var(--sand))]/40 light-halo-sm" data-testid="insight-panel">
          <div className="flex items-start gap-3">
            {/* Guide-generated insight — breathing emblem signals that
                N8 Guide is reflecting on the entry. Steps up to active
                while the insight is being generated. */}
            <N8Emblem
              mode="simplified"
              size={32}
              breathing
              active={loadingInsight}
              ariaLabel="N8 Guide emblem"
              testid="journal-insight-emblem"
              className="shrink-0 mt-0.5"
            />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Insight</div>
              <p className="mt-4 font-display text-xl leading-snug min-h-[6rem]">
                {insight || "Write a few lines, then ask the guide to reflect back what it hears."}
              </p>
            </div>
          </div>
        </aside>
      </section>

      {/* "Days of cultivating" moved from the top of Journal to just
          before the Past entries list. Rhythm now reads: write today →
          see what you've cultivated → revisit past entries. */}
      {streak && <StreakCard streak={streak} />}

      <section className="space-y-4 reveal reveal-2">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <h2 className="font-display text-3xl">Past entries</h2>
          <div className="relative w-full sm:w-72" data-testid="journal-search">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" aria-hidden />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a memory in one word…"
              data-testid="journal-search-input"
              className="pl-9 pr-9 bg-transparent rounded-full border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid="journal-search-clear"
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-testid="journal-filter-chips">
          <button
            type="button"
            onClick={() => { setShowStarredOnly(false); setEntryTypeFilter("all"); }}
            data-testid="journal-filter-all"
            aria-pressed={!showStarredOnly && entryTypeFilter === "all"}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] tab-link border hairline ${
              (!showStarredOnly && entryTypeFilter === "all")
                ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))] border-transparent"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
            }`}
          >
            All entries
            <span className="ml-1.5 opacity-70 tabular-nums normal-case">{entries.length}</span>
          </button>
          <button
            type="button"
            onClick={() => { setShowStarredOnly(true); setEntryTypeFilter("all"); }}
            data-testid="journal-filter-starred"
            aria-pressed={showStarredOnly}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] tab-link border hairline inline-flex items-center gap-1.5 ${
              showStarredOnly
                ? "bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] border-transparent"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
            }`}
          >
            <Star size={11} className={showStarredOnly ? "fill-[hsl(var(--paper))]" : ""} />
            Starred insights
            <span className="ml-0.5 opacity-70 tabular-nums normal-case">{starredCount}</span>
          </button>
          <button
            type="button"
            onClick={() => { setEntryTypeFilter(entryTypeFilter === "soul-work" ? "all" : "soul-work"); setShowStarredOnly(false); }}
            data-testid="journal-filter-soul-work"
            aria-pressed={entryTypeFilter === "soul-work"}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] tab-link border hairline inline-flex items-center gap-1.5 ${
              entryTypeFilter === "soul-work"
                ? "bg-[hsl(var(--clay))] text-[hsl(var(--paper))] border-transparent"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))]"
            }`}
          >
            <Feather size={11} />
            Soul Work
            <span className="ml-0.5 opacity-70 tabular-nums normal-case">{soulWorkCount}</span>
          </button>
          {showStarredOnly && (
            <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic">
              The lines that landed. Kept for later.
            </span>
          )}
        </div>

        {visibleEntries.length === 0 && (
          query.trim() ? (
            <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="entries-empty">
              {searching ? "Looking…" : `Nothing found for “${query.trim()}”. Try a softer word.`}
            </div>
          ) : showStarredOnly ? (
            <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="entries-empty-starred">
              No starred insights yet. Tap the star next to a Future You insight on Home, or bookmark one from <N8 /> Guide, and it will land here.
            </div>
          ) : (
            <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="entries-empty">Nothing yet. The page waits.</div>
          )
        )}
        {visibleEntries.map((e) => (
          <EntryCard
            key={e.id}
            entry={e}
            query={query}
            starred={isStarredInsight(e)}
            isSoulWork={isSoulWorkEntry(e)}
            onExploreSoulWork={() => navigate(`/rituals?soul=work&seed=${encodeURIComponent(e.id)}`)}
            onSaved={(updated) => setEntries((prev) => prev.map((x) => x.id === updated.id ? updated : x))}
            onDeleted={(id) => setEntries((prev) => prev.filter((x) => x.id !== id))}
          />
        ))}
      </section>
    </div>
  );
}

function EntryCard({ entry, query, starred, isSoulWork, onExploreSoulWork, onSaved, onDeleted }) {
  const [editing, setEditing] = React.useState(false);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [draftContent, setDraftContent] = React.useState(entry.content || "");
  const [draftPrompt, setDraftPrompt] = React.useState(entry.prompt || "");
  const [draftMood, setDraftMood] = React.useState(entry.mood_tag || "neutral");
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const startEditing = () => {
    setDraftContent(entry.content || "");
    setDraftPrompt(entry.prompt || "");
    setDraftMood(entry.mood_tag || "neutral");
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraftContent(entry.content || "");
    setDraftPrompt(entry.prompt || "");
    setDraftMood(entry.mood_tag || "neutral");
  };

  const saveEdits = async () => {
    const text = draftContent.trim();
    if (!text) { toast.error("An entry needs a body."); return; }
    setSaving(true);
    try {
      const { data } = await endpoints.updateJournal(entry.id, {
        content: text,
        prompt: draftPrompt.trim(),
        mood_tag: draftMood,
      });
      onSaved?.(data);
      setEditing(false);
      toast.success("Entry updated.");
    } catch {
      toast.error("Couldn't save. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async () => {
    setDeleting(true);
    try {
      await endpoints.deleteJournal(entry.id);
      onDeleted?.(entry.id);
      toast.success("Entry removed.");
    } catch {
      toast.error("Couldn't delete just yet.");
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <article data-testid={`entry-${entry.id}`} className="p-6 border hairline rounded-md bg-[hsl(var(--paper))]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
          {new Date(entry.created_at).toLocaleString()} · {editing ? draftMood : entry.mood_tag}
        </div>
        <div className="flex items-center gap-2">
          {isSoulWork && !editing && (
            <div
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--clay))]"
              data-testid={`entry-soul-work-badge-${entry.id}`}
            >
              <Feather size={10} /> Soul Work
            </div>
          )}
          {starred && !editing && (
            <div
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--terracotta))]"
              data-testid={`entry-starred-badge-${entry.id}`}
            >
              <Star size={10} className="fill-[hsl(var(--terracotta))]" /> Saved
            </div>
          )}
          {!editing && (
            <>
              <button
                type="button"
                onClick={startEditing}
                data-testid={`entry-edit-${entry.id}`}
                aria-label="Edit entry"
                className="rounded-full p-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))] hover:bg-[hsl(var(--sand))] tab-link"
              >
                <Pencil size={13} />
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                data-testid={`entry-delete-${entry.id}`}
                aria-label="Delete entry"
                className="rounded-full p-1.5 text-[hsl(var(--muted-foreground))] hover:text-red-700 hover:bg-red-50 tab-link"
              >
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-3 space-y-3" data-testid={`entry-edit-form-${entry.id}`}>
          <Input
            value={draftPrompt}
            onChange={(e) => setDraftPrompt(e.target.value)}
            placeholder="Prompt (optional)"
            data-testid={`entry-edit-prompt-${entry.id}`}
            className="italic"
          />
          <Textarea
            value={draftContent}
            onChange={(e) => setDraftContent(e.target.value)}
            rows={5}
            data-testid={`entry-edit-content-${entry.id}`}
            className="text-base leading-relaxed"
          />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Select value={draftMood} onValueChange={setDraftMood}>
              <SelectTrigger className="w-40" data-testid={`entry-edit-mood-${entry.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {MOODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={cancelEditing}
                data-testid={`entry-edit-cancel-${entry.id}`}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={saveEdits}
                disabled={saving || !draftContent.trim()}
                data-testid={`entry-edit-save-${entry.id}`}
                className="bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
              >
                {saving ? "Holding…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {entry.prompt && (
            <p className="mt-3 italic font-display text-lg text-[hsl(var(--clay))]">
              <Highlight text={entry.prompt} q={query} />
            </p>
          )}
          <p className="mt-2 leading-relaxed whitespace-pre-wrap">
            <Highlight text={entry.content} q={query} />
          </p>
          {Array.isArray(entry.attachments) && entry.attachments.length > 0 && (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`entry-attachments-${entry.id}`}>
              {entry.attachments.map((p) => (
                <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="block aspect-square rounded-md overflow-hidden border hairline bg-[hsl(var(--sand))]/40">
                  <img src={`${API}/files/${p}`} alt="entry attachment" className="w-full h-full object-cover" onError={(ev) => { ev.currentTarget.style.opacity = "0.3"; }} />
                </a>
              ))}
            </div>
          )}
          {!isSoulWork && onExploreSoulWork && (
            <div className="mt-4">
              <button
                type="button"
                onClick={onExploreSoulWork}
                data-testid={`entry-explore-soul-work-${entry.id}`}
                className="inline-flex items-center gap-2 text-xs text-[hsl(var(--clay))] uppercase tracking-[0.22em] hover:opacity-80"
              >
                <Feather size={11} /> Explore This Through Soul Work
              </button>
            </div>
          )}
        </>
      )}

      {confirmingDelete && !editing && (
        <div
          className="mt-4 p-3 rounded-md border border-red-200 bg-red-50/60 flex items-center justify-between gap-3 flex-wrap"
          data-testid={`entry-delete-confirm-${entry.id}`}
        >
          <div className="text-sm text-red-900">Remove this entry? This cannot be undone.</div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => setConfirmingDelete(false)}
              data-testid={`entry-delete-cancel-${entry.id}`}
              disabled={deleting}
            >
              Keep it
            </Button>
            <Button
              onClick={removeEntry}
              disabled={deleting}
              data-testid={`entry-delete-confirm-btn-${entry.id}`}
              className="bg-red-700 hover:bg-red-800 text-white"
            >
              {deleting ? "Removing…" : "Remove"}
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}


function Highlight({ text, q }) {
  const term = (q || "").trim();
  if (!term || !text) return <>{text}</>;
  const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === term.toLowerCase()
          ? <mark key={i} className="bg-[hsl(var(--sand))] text-[hsl(var(--fg))] rounded-sm px-0.5">{p}</mark>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </>
  );
}

function StreakCard({ streak }) {
  const { current_streak, longest_streak, wrote_today, at_risk, active_days_30, entries_this_week } = streak;
  const subtitle = wrote_today
    ? "You cultivated the page today."
    : at_risk
      ? "Write today to keep the streak alive."
      : current_streak > 0
        ? "Return whenever the page calls."
        : "A blank page is a good beginning.";

  const numberLabel = current_streak === 1 ? "day" : "days";

  return (
    <section
      className="p-6 md:p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-1"
      data-testid="journal-streak-card"
    >
      <div className="grid md:grid-cols-[auto,1fr,auto] gap-6 md:gap-8 items-center">
        <div className="flex items-baseline gap-3">
          <div
            className={`font-display leading-none tracking-tight text-6xl md:text-7xl ${current_streak > 0 ? "text-[hsl(var(--clay))]" : "text-[hsl(var(--muted-foreground))]"}`}
            data-testid="streak-current"
          >
            {current_streak}
          </div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
            {numberLabel} of cultivating
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
            {wrote_today ? <Check size={12} className="text-[hsl(var(--moss))]" /> : <Flame size={12} className="text-[hsl(var(--terracotta))]" />}
            {wrote_today ? "Written today" : at_risk ? "One day left" : "Your steady streak"}
          </div>
          <p className="mt-1 font-display text-xl md:text-2xl italic leading-snug">{subtitle}</p>
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="streak-heatmap" aria-label="Journal activity, last 30 days">
            {active_days_30.map((d) => (
              <span
                key={d.date}
                title={`${d.date} — ${d.active ? "wrote" : "quiet"}`}
                className={`w-2.5 h-2.5 rounded-[3px] ${d.active ? "bg-[hsl(var(--clay))]" : "bg-[hsl(var(--sand))]"}`}
              />
            ))}
          </div>
        </div>

        <div className="flex md:flex-col items-start md:items-end gap-6 md:gap-3">
          <Stat label="Longest" value={longest_streak} testid="streak-longest" />
          <Stat label="This week" value={`${entries_this_week}/7`} testid="streak-this-week" />
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="font-display text-2xl leading-none mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}
